/**
 * Coverage-related data fetchers
 */
import { useMutation, useQuery } from "@tanstack/react-query";

import { createOrUpdateCoverage } from "@/src/actions/coverage";
import { toast } from "@/src/hooks/use-toast";
import { PATIENTS_CACHE_KEY } from "@/src/lib/data-fetchers/patients-fetchers";
import { REFERRALS_CACHE_KEY } from "@/src/lib/data-fetchers/referrals-fetchers";
import { TASK_CACHE_KEY, TASKS_CACHE_KEY } from "@/src/lib/data-fetchers/tasks-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { Coverage } from "@/src/models/coverage";

export function useCoverageUpdateMutation() {
  return useMutation({
    mutationFn: async (coverage: Coverage) => {
      return await createOrUpdateCoverage(coverage, true);
    },
    onError: (error) => {
      logger.error("Error updating coverage:", { error });

      toast({
        title: "Error updating coverage",
        description: "There was an error saving the coverage information. Please try again.",
        variant: "destructive",
      });
    },
    meta: {
      invalidates: [[PATIENTS_CACHE_KEY], [REFERRALS_CACHE_KEY], [TASKS_CACHE_KEY], [TASK_CACHE_KEY]],
    },
  });
}

/**
 * Fetch the top insurance payers
 */
export function useInsuranceTopPayers() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["insurance-top-payers"],
    queryFn: async () => {
      const res = await fetch("/api/insurance-top-payers");

      if (!res.ok) throw new Error("Failed to fetch insurance top payers");

      return res.json();
    },
    staleTime: Infinity,
  });

  return {
    topInsurancePayers: data || [],
    isLoading,
    isError,
  };
}
